import type { InputNumberProps } from 'antd'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

/** Fuso orario canonico dell'applicazione. */
export const TZ_ROME = 'Europe/Rome'

/**
 * Formatta una data/ora SEMPRE in fuso `Europe/Rome`, formato canonico
 * `DD/MM/YYYY HH:mm:ss` (vedi CLAUDE.md).
 *
 * REGOLA FUSO ORARIO: per VISUALIZZARE una data usa SEMPRE questa funzione,
 * MAI `dayjs(x).format('DD/MM/YYYY HH:mm:ss')` grezzo. Il backend invia gli
 * istanti in UTC: senza fissare `Europe/Rome` la data verrebbe resa nel fuso
 * del browser dell'utente, che fuori dall'Italia mostrerebbe un'ora sbagliata.
 *
 * NB: NON usarla per i valori dei DatePicker (input) — lì serve un `dayjs`
 * grezzo perché l'utente sta scegliendo un orario.
 */
export function formatDateTime(
  value: string | number | Date | null | undefined,
  fmt = 'DD/MM/YYYY HH:mm:ss',
): string {
  if (value === null || value === undefined || value === '') return '—'
  const d = dayjs(value)
  if (!d.isValid()) return '—'
  return d.tz(TZ_ROME).format(fmt)
}

/** Solo data (`DD/MM/YYYY`) in fuso `Europe/Rome`. */
export function formatDate(value: string | number | Date | null | undefined): string {
  return formatDateTime(value, 'DD/MM/YYYY')
}

/**
 * Formatta un importo in euro con la locale italiana.
 * Esempio: 15000 → "€ 15.000,00"
 *
 * Usare per la VISUALIZZAZIONE (table, card, stat, badge).
 * Per gli INPUT usare moneyInputProps.
 */
export function fmtEuro(value: number | string | null | undefined): string {
  const num = typeof value === 'string' ? Number(value) : Number(value ?? 0)
  if (Number.isNaN(num)) return '€ —'
  const [int, dec] = num.toFixed(2).split('.')
  return `€ ${int.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${dec}`
}

/**
 * Props da spreadare su ogni InputNumber che rappresenta un importo in euro.
 *
 * Formatta in notazione italiana durante l'editing (punto migliaia, virgola decimale).
 * Es: digiti "1150" → vedi "1.150,00"
 *      digiti "1150,43" → vedi "1.150,43"
 *
 * Uso corretto:
 *   <InputNumber {...moneyInputProps} min={0} precision={2} />
 *
 * Mentre l'utente digita (info.userTyping) il testo NON va riformattato:
 * antd con formatter custom riapplica il formatter a ogni keystroke, e
 * riformattare qui (toFixed) impedisce di digitare la virgola dei decimali
 * ("12," diventava "12,00" / "12,,00" e il parser troncava all'intero).
 */
function formattaNotazioneItaliana(num: number | string | null | undefined): string {
  if (num === undefined || num === null || num === '') return ''
  const n = Number(num)
  if (Number.isNaN(n)) return ''

  // Converti a stringa con 2 decimali fissi
  const partes = n.toFixed(2).split('.')
  const integerPart = partes[0]
  const decimalPart = partes[1]

  // Aggiungi punti come separatori migliaia
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.')

  // Ritorna con virgola come separatore decimale
  return `${formattedInteger},${decimalPart}`
}

export const moneyInputProps: Pick<InputNumberProps, 'formatter' | 'parser' | 'precision'> = {
  precision: 2,
  formatter: (val, info) => {
    if (info?.userTyping) return info.input
    return formattaNotazioneItaliana(val)
  },
  parser: (display) => {
    if (!display) return '' as unknown as number
    // Parse: accetta notazione italiana (punto migliaia, virgola decimale)
    // Rimuove i punti (migliaia) e sostituisce virgola con punto per il parsing
    const cleaned = String(display)
      .replace(/€\s?/g, '')      // rimuovi €
      .replace(/\./g, '')         // rimuovi i punti (separatori migliaia)
      .replace(',', '.')          // converti virgola decimale in punto
    const n = parseFloat(cleaned)
    return (isNaN(n) ? '' : n) as unknown as number
  },
}
